/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { generateSimplestList, recalcRequiredLocalAttribute } from '@touchplan/common';
import { DefaultSplitDataRenderType, SplitData } from '@touchplan/common';
import { OrganizationProject } from './OrganizationProject';

export enum OrganizationType {
	'Organization',
	'Application',
}

// Path: /organizations/organizationId
export interface IOrganizationFields {
	organizationId: string;
	name?: string;
	description: string;
	url: string;
	createdAt?: null;
	deletedAt?: null;
	parentOrganizationId?: string;
	salesforceAccountId?: string;
	type: OrganizationType;
	inheritProjects: boolean;
	selectedProjects?: Array<{ orgProject: OrganizationProject; selected: boolean }>;
	requiresScheduleDisclaimerAcknowledgment?: boolean;
}

export class Organization extends SplitData<IOrganizationFields, DefaultSplitDataRenderType> {
	public asyncSelectionLoaded = false;
	protected attributeList = Object.assign(
		generateSimplestList([
			'organizationId',
			'name',
			'description',
			'url',
			'createdAt',
			'deletedAt',
			'parentOrganizationId',
			'salesforceAccountId',
			'type',
			'inheritProjects',
			'projectIds',
			'requiresScheduleDisclaimerAcknowledgment',
		]),
		{ selectedProjects: recalcRequiredLocalAttribute }
	);

	private saveableProjectIds(renderType?: DefaultSplitDataRenderType) {
		renderType = this.defType(renderType);
		const view: IOrganizationFields = this[renderType];

		const ids: string[] = [];

		view?.selectedProjects?.forEach((p) => {
			if (p.selected) {
				ids.push(p.orgProject.$id);
			}
		});
		return ids;
	}

	public switchViews(
		renderType: DefaultSplitDataRenderType,
		cancel: boolean = false,
		targetRenderOverride?: DefaultSplitDataRenderType
	): void {
		super.switchViews(renderType, cancel, targetRenderOverride);
		//reset the loaded flag on "unselection". For future stuff should consider keeping the things driving loaded alive for longer
		if (renderType === this.primaryType) {
			this.asyncSelectionLoaded = false;
		}
	}

	public generateDatabaseOutput(renderType?: DefaultSplitDataRenderType) {
		const output = super.generateDatabaseOutput(renderType);
		output['projectIds'] = this.saveableProjectIds(renderType);
		return output;
	}

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}
}
